import { AuditTask } from "../../types";
import { PanelHero } from "../ui/panel";
import TaskProgressBar from "./TaskProgressBar";
import UserTaskPerformance from "./UserTaskPerformance";

interface UserAuditPanelProps {
  tasks: AuditTask[];
  loading: boolean;
}

function formatDate(value: string) {
  try {
    return new Date(value).toLocaleDateString("it-IT", {
      day: "numeric",
      month: "short",
    });
  } catch {
    return value;
  }
}

function daysUntil(dueDate: string) {
  const due = new Date(dueDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);
  return Math.ceil((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

function CircularProgress({
  percent,
  overdue,
  done,
  tone = "onLight",
}: {
  percent: number;
  overdue?: boolean;
  done?: boolean;
  tone?: "onLight" | "onDark";
}) {
  const r = 36;
  const c = 2 * Math.PI * r;
  const offset = c - (percent / 100) * c;
  const onDark = tone === "onDark";
  const stroke = done ? "#34d399" : overdue ? "#f87171" : onDark ? "#c7d2fe" : "#6366f1";
  const trackStroke = onDark ? "rgba(255,255,255,0.28)" : "rgba(46,67,106,0.14)";

  return (
    <div className="relative h-24 w-24 shrink-0">
      <svg className="-rotate-90" viewBox="0 0 96 96">
        <circle cx="48" cy="48" r={r} fill="none" stroke={trackStroke} strokeWidth="8" />
        <circle
          cx="48"
          cy="48"
          r={r}
          fill="none"
          stroke={stroke}
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
          className="transition-all duration-700"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span
          className={`text-xl font-extrabold tabular-nums ${
            onDark ? "text-white" : "text-secondary"
          }`}
        >
          {percent}%
        </span>
      </div>
    </div>
  );
}

export default function UserAuditPanel({ tasks, loading }: UserAuditPanelProps) {
  const activeTasks = tasks.filter((t) => t.status === "active");
  const hasAnyTask = tasks.length > 0;

  const totalTarget = activeTasks.reduce((s, t) => s + t.target_count, 0);
  const totalDone = activeTasks.reduce(
    (s, t) => s + Math.min(t.completed_count, t.target_count),
    0
  );
  const overallPct = totalTarget > 0 ? Math.round((totalDone / totalTarget) * 100) : 0;

  return (
    <div className="space-y-8 text-left">
      <PanelHero
        variant="brand"
        eyebrow="Le tue task"
        title="Obiettivi settimanali"
        description="Completa i lead con tutti i dati e registra almeno un contatto (+) dopo l'assegnazione. Ricarica la pagina per aggiornare il progresso."
        action={
          activeTasks.length > 0 ? (
            <CircularProgress
              percent={overallPct}
              done={overallPct >= 100}
              overdue={activeTasks.some((t) => t.overdue)}
              tone="onDark"
            />
          ) : undefined
        }
      />

      {loading ? (
        <div className="grid gap-4 md:grid-cols-2">
          {[1, 2].map((i) => (
            <div key={i} className="h-48 animate-pulse rounded-2xl bg-secondary/10" />
          ))}
        </div>
      ) : activeTasks.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-secondary/15 bg-gradient-to-b from-white to-secondary/[0.02] px-8 py-16 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-secondary/5 text-3xl">
            📋
          </div>
          <p className="text-lg font-extrabold text-secondary">Nessuna task attiva</p>
          <p className="mx-auto mt-2 max-w-sm text-sm text-secondary/50">
            Quando il tuo admin ti assegnerà un obiettivo, comparirà qui con la progress bar.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          {activeTasks.map((task) => {
            const days = daysUntil(task.due_date);
            const dueSoon = !task.overdue && days >= 0 && days <= 3;
            const pct =
              task.target_count > 0
                ? Math.min(100, Math.round((task.completed_count / task.target_count) * 100))
                : 0;
            const done = task.completed_count >= task.target_count;

            return (
              <div
                key={task.id}
                className={`group relative overflow-hidden rounded-2xl border bg-white p-5 shadow-lg transition hover:-translate-y-0.5 hover:shadow-xl ${
                  task.overdue
                    ? "border-red-200 ring-2 ring-red-100"
                    : done
                      ? "border-emerald-200 ring-2 ring-emerald-100"
                      : "border-secondary/10"
                }`}
              >
                <div
                  className={`absolute inset-x-0 top-0 h-1 ${
                    done
                      ? "bg-gradient-to-r from-emerald-400 to-teal-500"
                      : task.overdue
                        ? "bg-gradient-to-r from-red-500 to-rose-500"
                        : "bg-gradient-to-r from-indigo-500 to-violet-500"
                  }`}
                />
                <div className="flex gap-4">
                  <CircularProgress percent={pct} overdue={task.overdue} done={done} />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-wider text-indigo-500">
                          Contatta lead
                        </p>
                        <p className="text-lg font-extrabold text-secondary">
                          {task.target_count} lead
                        </p>
                      </div>
                      {task.overdue ? (
                        <span className="rounded-full bg-red-100 px-2.5 py-1 text-[10px] font-extrabold uppercase text-red-700">
                          Scaduta
                        </span>
                      ) : dueSoon ? (
                        <span className="rounded-full bg-amber-100 px-2.5 py-1 text-[10px] font-extrabold uppercase text-amber-800">
                          {days === 0 ? "Oggi" : `${days}g`}
                        </span>
                      ) : (
                        <span className="rounded-full bg-indigo-100 px-2.5 py-1 text-[10px] font-extrabold uppercase text-indigo-700">
                          Attiva
                        </span>
                      )}
                    </div>
                    <div className="mt-3">
                      <TaskProgressBar
                        completed={task.completed_count}
                        target={task.target_count}
                        overdue={task.overdue}
                        dueSoon={dueSoon}
                      />
                    </div>
                    <p className="mt-3 text-xs text-secondary/50">
                      Scadenza{" "}
                      <span className="font-bold text-secondary">{formatDate(task.due_date)}</span>
                    </p>
                    {task.notes && (
                      <p className="mt-2 rounded-lg bg-secondary/[0.04] p-2 text-xs text-secondary/70">
                        {task.notes}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!loading && hasAnyTask && <UserTaskPerformance tasks={tasks} />}
    </div>
  );
}
